import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { prepareHeaders } from '../utils/rtk-headers';
import { getEnvironment } from '../env/env';
import { refreshToken } from 'src/services/FetchRequest';
import { PATH_AUTH_SPA } from 'src/constants/spa-routes';
import {
  refetchQuerysWithAccountParam,
  refetchQuerysWithProjectTypeParam,
} from 'src/constants/refetch-account-params';
import { ProductsTypeBack } from 'src/constants/products';
import { ERROR_MESSAGE_FRIENDLY } from 'src/shared/constants';
import { clearOnLogout } from 'src/utils/clear.util';

let isRefreshing = false;

const getUser = (): Record<string, any> => {
  let user: any = localStorage.getItem('user');
  user = user && user !== 'undefined' && JSON.parse(user);

  return user || {};
};

const getRefreshToken = () => {
  const user = getUser();

  return user && (user.refresh || user.refresh_token);
};

const staggeredBaseQueryWithBailOut = retry(
  async (args, api, extraOptions: any) => {
    const environment = await getEnvironment();
    const baseUrl = environment.APP_BASE_URL;

    const state = api.getState() as any;
    const {
      companySlices: { managedAccountId },
      projectsSlices: { projectType },
    } = state;

    const searchParams = new URLSearchParams(location.search);
    const searchProjectType = searchParams.get('type');

    const projectTypeVar = searchProjectType || projectType;

    const urlPath = `/api/${args.url}`;

    // const url = new URL(urlPath, baseUrl);
    const url = args.url.startsWith('https://') ? new URL(args.url) : new URL(urlPath, baseUrl);

    if (
      !args?.params?.account &&
      managedAccountId &&
      refetchQuerysWithAccountParam.includes(api.endpoint)
    ) {
      url.searchParams.set('account', managedAccountId);
    }

    if (projectTypeVar && refetchQuerysWithProjectTypeParam.includes(api.endpoint)) {
      url.searchParams.set('project_type', ProductsTypeBack[projectTypeVar]);
    }

    const updatedArgs = { ...args, url: url.toString() };
    const fetchBaseQueryArgs: any = {
      baseUrl,
      prepareHeaders,
    };

    if (extraOptions?.responseHandler) {
      fetchBaseQueryArgs.responseHandler = extraOptions.responseHandler;
    }

    if (extraOptions?.mode) {
      fetchBaseQueryArgs.mode = extraOptions.mode;
    }

    const result = await fetchBaseQuery(fetchBaseQueryArgs)(updatedArgs, api, extraOptions);

    if (result.error?.status === 400) {
      retry.fail(result.error);
    }

    if (result.error?.status === 401) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const refresh = getRefreshToken();
          const token = await refreshToken(refresh);

          if (token.error?.status === 401) {
            clearOnLogout();
            window.location.assign(PATH_AUTH_SPA.ROOT);
            retry.fail(result.error);
          }

          if (token.access_token) {
            const user = getUser();
            const newUser = {
              ...user,
              access_token: token.access_token,
            };
            localStorage.setItem('user', JSON.stringify(newUser));
          } else {
            localStorage.removeItem('user');
            window.location.assign(PATH_AUTH_SPA.ROOT);
            return token;
          }
        } catch (error) {
          return console.log('staggeredBaseQueryWithBailOut error', error);
        } finally {
          isRefreshing = false;
        }
      }
    }

    if (['FETCH_ERROR', 500].includes(result.error?.status)) {
      (result.error as any).error = ERROR_MESSAGE_FRIENDLY;
    }

    return result;
  },
  {
    maxRetries: 2,
  },
);

export const baseApi = createApi({
  reducerPath: 'baseApi',
  baseQuery: staggeredBaseQueryWithBailOut,
  tagTypes: [
    'User',
    'UsersAll',
    'Projects',
    'UserMe',
    'Accounts',
    'ProjectById',
    'ProjectTeamMembers',
    'CreateUser',
    'PaymentMethods',
    'Payments',
    'ProjectPlans',
    'ProjectIntegration',
    'ProjectFiles',
    'ProjectChats',
    'SubscriptionPermission',
    'CoCustomerAnalyticsAggregated',
    'CoCustomerAnalyticsTimeSeries',
    'CoCustomerAnalyticsChatsCount',
    'PlaygroundChat',
    'KnowledgeBase',
    'KnowledgeBaseById',
    'defaultTemplateFields',
    'customTemplates',
    'ConfigurationSettings',
    'ConfigurationSettingsPrompt',
  ],
  endpoints: () => ({}),
});
